import is from '@sindresorhus/is';

export type AudioRecorder = {
    readonly error: string | null;
    readonly start: () => Promise<void>;
    readonly stop: () => Promise<Blob>;
};

export type AudioRecorderDependencies = {
    readonly createMediaRecorder: (mediaStream: MediaStream) => MediaRecorder;
    readonly getUserMedia: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
};

export const createAudioRecorder = (dependencies: AudioRecorderDependencies): AudioRecorder => {
    const { createMediaRecorder, getUserMedia } = dependencies;

    let recorder: MediaRecorder | null = null;
    let chunks: Blob[] = [];
    let stream: MediaStream | null = null;
    let error: string | null = null;

    const start: AudioRecorder['start'] = async () => {
        if (recorder?.state === 'recording') {
            return;
        }

        try {
            stream = await getUserMedia({
                audio: {
                    channelCount: { ideal: 1 },
                    echoCancellation: { ideal: true },
                    noiseSuppression: { ideal: true },
                    sampleRate: { ideal: 48_000 },
                },
            });
            recorder = createMediaRecorder(stream);
            chunks = [];

            recorder.addEventListener('dataavailable', (e) => {
                if (e.data.size > 0) chunks.push(e.data);
            });

            recorder.start();
            error = null;
        } catch (err) {
            error = err instanceof Error ? err.message : 'Failed to start audio recording.';
        }
    };

    const stop: AudioRecorder['stop'] = async (): Promise<Blob> => {
        if (recorder?.state !== 'recording') {
            error = 'No recording in progress.';

            return new Blob();
        }

        return new Promise((resolve) => {
            if (!is.nullOrUndefined(recorder)) {
                const mimeType = recorder.mimeType;
                recorder.addEventListener('stop', () => {
                    const blob = new Blob(chunks, { type: mimeType });
                    // biome-ignore lint/suspicious/useIterableCallbackReturn: suppressed due to migration
                    stream?.getTracks().forEach((track) => track.stop());
                    resolve(blob);
                });

                recorder.stop();
            }
        });
    };

    return {
        get error() {
            return error;
        },
        start,
        stop,
    };
};
